import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, ChevronLeft, ChevronRight, Download, X, MoreVertical, Eye, Archive, ArchiveRestore, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { api, unwrap, API_BASE } from "@/lib/api";
import { getToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";

const NON_DELETABLE_STATUSES = new Set(["paid", "assigned", "in_progress", "completed", "refunded"]);

const STATUS_BADGE = {
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-cyan-50 text-cyan-700 border-cyan-200",
  assigned: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  completed: "bg-teal-50 text-teal-700 border-teal-200",
  cancelled: "bg-stone-100 text-stone-600 border-stone-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
  pending_coordination: "bg-amber-50 text-amber-700 border-amber-200",
  coordination_confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  coordination_cancelled: "bg-stone-100 text-stone-600 border-stone-200",
};

const STATUSES = ["", "pending_payment", "paid", "scheduled", "assigned", "in_progress", "completed", "cancelled", "refunded", "pending_coordination", "coordination_confirmed", "coordination_cancelled"];
const SOURCES = [
  { v: "", l: "All sources" },
  { v: "wizard", l: "Wizard" },
  { v: "ical_sync", l: "Airbnb sync" },
  { v: "admin", l: "Admin" },
];
const SOURCE_LABEL = { wizard: "Wizard", ical_sync: "Airbnb", admin: "Admin" };

export default function AdminBookings() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [cleaners, setCleaners] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("paid");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort });
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      if (source) params.set("source", source);
      if (assignedFilter) params.set("cleaner_id", assignedFilter);
      if (unassignedOnly) params.set("unassigned", "true");
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (showArchived) params.set("include_archived", "true");
      const r = await api.get(`/admin/bookings?${params.toString()}`);
      const d = unwrap(r);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, source, assignedFilter, unassignedOnly, dateFrom, dateTo, sort, showArchived]);

  /* eslint-disable */
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/cleaners").then((r) => setCleaners(unwrap(r)?.items || [])).catch(() => {}); }, []);
  /* eslint-enable */

  const pages = Math.max(1, Math.ceil(total / pageSize));

  const downloadCsv = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    const url = `${API_BASE}/admin/bookings/export.csv${params.toString() ? "?" + params.toString() : ""}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `everclean-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const quickRange = (kind) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (kind === "today") { setDateFrom(iso(now)); setDateTo(iso(now)); }
    else if (kind === "week") {
      const start = new Date(now); start.setDate(now.getDate() - now.getDay());
      setDateFrom(iso(start)); setDateTo(iso(now));
    } else if (kind === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      setDateFrom(iso(start)); setDateTo(iso(now));
    } else { setDateFrom(""); setDateTo(""); }
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const applyBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Change status of ${selected.size} bookings to "${bulkStatus}"?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/bookings/bulk-status", {
        booking_ids: Array.from(selected),
        status: bulkStatus,
        notify_customers: false,
      });
      const d = unwrap(r);
      toast.success(`Updated ${d.modified} bookings`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Bulk update failed");
    } finally { setBulkBusy(false); }
  };

  const archiveBooking = async (b, archived) => {
    setBusyId(b.id);
    try {
      await api.patch(`/admin/bookings/${b.id}/archive`, { archived });
      toast.success(archived ? "Booking archived" : "Booking restored");
      load();
    } catch { toast.error("Action failed"); }
    finally { setBusyId(null); }
  };

  const sendPaymentReminder = async (b) => {
    setBusyId(b.id);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/resend-payment-link`);
      const d = unwrap(r);
      toast.success(`Payment reminder sent to ${d.customer_email}`);
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Could not send the reminder"); }
    finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await api.delete(`/admin/bookings/${deleteTarget.id}`);
      toast.success("Booking permanently deleted");
      setDeleteTarget(null);
      setDeleteConfirmText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Delete failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-bookings">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Bookings</h1>
          <p className="text-[#4B5563] text-sm mt-1">{total} total</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer" data-testid="show-archived-bookings-toggle">
            <Switch checked={showArchived} onCheckedChange={(v) => { setShowArchived(v); setPage(1); }} data-testid="show-archived-bookings-switch" />
            Show archived
          </label>
          <Button onClick={downloadCsv} variant="outline" className="rounded-xl" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl p-4 border border-[#E5E7EB] space-y-3" data-testid="bookings-filters">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#9CA3AF]" />
            <Input placeholder="Search by name, email, phone or booking #"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="rounded-xl pl-9" data-testid="bookings-search" />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="bookings-status-filter">
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s ? s.replace("_", " ") : "All statuses"}</option>
            ))}
          </select>
          <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}
            className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="bookings-source-filter">
            {SOURCES.map((s) => (
              <option key={s.v} value={s.v}>{s.l}</option>
            ))}
          </select>
          <select value={assignedFilter} onChange={(e) => { setAssignedFilter(e.target.value); setPage(1); }}
            className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="bookings-assigned-filter">
            <option value="">All cleaners</option>
            {cleaners.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="bookings-sort">
            <option value="created_desc">Newest first</option>
            <option value="created_asc">Oldest first</option>
            <option value="total_desc">Total ↓</option>
            <option value="total_asc">Total ↑</option>
            <option value="name_asc">Customer A→Z</option>
            <option value="status_asc">Status</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <span className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mr-1">Date range:</span>
          {[
            { k: "today", l: "Today" },
            { k: "week", l: "This week" },
            { k: "month", l: "This month" },
            { k: "all", l: "All time" },
          ].map((c) => (
            <button key={c.k} onClick={() => quickRange(c.k)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E5E7EB] hover:border-[#0EA5A4] transition-colors"
              data-testid={`date-quick-${c.k}`}>{c.l}</button>
          ))}
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="rounded-xl h-9 w-auto text-xs" data-testid="date-from" />
          <span className="text-[#9CA3AF]">→</span>
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="rounded-xl h-9 w-auto text-xs" data-testid="date-to" />
          <label className="flex items-center gap-2 ml-2 cursor-pointer text-xs font-medium" data-testid="unassigned-toggle-label">
            <Checkbox checked={unassignedOnly} onCheckedChange={(v) => { setUnassignedOnly(!!v); setPage(1); }} data-testid="unassigned-toggle" />
            Unassigned only
          </label>
        </div>
      </Card>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {selected.size > 0 && (
          <div className="bg-[#0EA5A4]/10 border-b border-[#0EA5A4]/20 px-5 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
              className="h-9 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm" data-testid="bulk-status">
              {STATUSES.filter((s) => s && s !== "refunded").map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <Button onClick={applyBulk} disabled={bulkBusy}
              className="h-9 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="bulk-apply">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </Button>
            <Button onClick={() => setSelected(new Set())} variant="outline" size="sm" className="rounded-xl ml-auto" data-testid="bulk-clear">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        )}
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#4B5563]">No bookings found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                  <th className="px-3 py-3">
                    <Checkbox checked={items.length > 0 && selected.size === items.length}
                      onCheckedChange={toggleSelectAll} data-testid="bulk-select-all" />
                  </th>
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Zone</th>
                  <th className="px-5 py-3">Property</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Assigned to</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => {
                  const canDelete = !NON_DELETABLE_STATUSES.has(b.status);
                  return (
                  <tr key={b.id} className={`border-t border-[#E5E7EB] hover:bg-[#FAFAF9] ${b.is_archived ? "opacity-60" : ""}`} data-testid={`booking-row-${b.booking_number}`}>
                    <td className="px-3 py-3">
                      <Checkbox checked={selected.has(b.id)} onCheckedChange={() => toggleSelect(b.id)} data-testid={`bulk-select-${b.booking_number}`} />
                    </td>
                    <td className="px-5 py-3 font-mono">
                      <Link to={`/admin/bookings/${b.id}`} className="text-[#0EA5A4] hover:underline" data-testid={`booking-link-${b.booking_number}`}>{b.booking_number}</Link>
                      {b.is_archived && <Badge className="border bg-stone-100 text-stone-500 border-stone-200 font-medium ml-2 text-[10px]" data-testid={`archived-badge-${b.booking_number}`}>Archived</Badge>}
                    </td>
                    <td className="px-5 py-3">{b.customer.full_name}<div className="text-xs text-[#9CA3AF]">{b.customer.email}</div></td>
                    <td className="px-5 py-3">{b.service.zone_name}</td>
                    <td className="px-5 py-3 capitalize">{b.service.property_type} · {b.service.square_feet}sqft</td>
                    <td className="px-5 py-3 font-mono font-semibold">${b.pricing.total.toFixed(2)}</td>
                    <td className="px-5 py-3" data-testid={`booking-assigned-${b.booking_number}`}>
                      {b.cleaner_name ? <Link to={`/admin/cleaners`} className="text-[#0EA5A4] hover:underline font-medium">{b.cleaner_name}</Link> : <span className="text-[#9CA3AF]">—</span>}
                      {!b.cleaner_id && b.declined_cleaner_name && (
                        <div className="mt-1"><Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] font-medium" data-testid={`declined-flag-${b.booking_number}`}>Declined by {b.declined_cleaner_name}</Badge></div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className={`text-xs font-medium ${b.source === "ical_sync" ? "border-[#0EA5A4]/40 text-[#0F766E] bg-[#0EA5A4]/5" : ""}`} data-testid={`booking-source-${b.booking_number}`}>
                        {SOURCE_LABEL[b.source] || "Wizard"}
                      </Badge>
                    </td>
                    <td className="px-5 py-3"><Badge className={`border ${STATUS_BADGE[b.status]} font-medium capitalize`}>{b.status.replace("_", " ")}</Badge></td>
                    <td className="px-5 py-3 text-[#4B5563]">{formatDate(b.created_at, locale)}</td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8" disabled={busyId === b.id} data-testid={`booking-actions-${b.booking_number}`}>
                            {busyId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/bookings/${b.id}`} data-testid={`view-booking-${b.booking_number}`}>
                              <Eye className="w-4 h-4 mr-2" /> View
                            </Link>
                          </DropdownMenuItem>
                          {b.status === "pending_payment" && (
                            <DropdownMenuItem onClick={() => sendPaymentReminder(b)} data-testid={`send-reminder-${b.booking_number}`}>
                              <Send className="w-4 h-4 mr-2" /> Send payment reminder
                            </DropdownMenuItem>
                          )}
                          {b.is_archived ? (
                            <DropdownMenuItem onClick={() => archiveBooking(b, false)} data-testid={`restore-booking-${b.booking_number}`}>
                              <ArchiveRestore className="w-4 h-4 mr-2" /> Restore
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => archiveBooking(b, true)} data-testid={`archive-booking-${b.booking_number}`}>
                              <Archive className="w-4 h-4 mr-2" /> Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {canDelete ? (
                            <DropdownMenuItem onClick={() => { setDeleteTarget(b); setDeleteConfirmText(""); }} className="text-rose-600 focus:text-rose-700 focus:bg-rose-50" data-testid={`delete-booking-${b.booking_number}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete permanently
                            </DropdownMenuItem>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <DropdownMenuItem disabled className="text-rose-300" data-testid={`delete-booking-disabled-${b.booking_number}`}>
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete permanently
                                    </DropdownMenuItem>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-[220px]">
                                  Cannot permanently delete a booking with financial records. Archive instead.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
          <span className="text-[#4B5563]">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="bookings-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="bookings-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <AlertDialogContent data-testid="delete-booking-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this booking permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove booking <b>{deleteTarget?.booking_number}</b> from the database. This action cannot be undone.
              Type the booking number below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteTarget?.booking_number} className="rounded-xl font-mono" data-testid="delete-booking-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-booking-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteConfirmText !== deleteTarget?.booking_number}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40" data-testid="delete-booking-confirm-btn">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
